
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ProfilePhotoUpload from "@/components/ui/profile-photo-upload";
import { CompanyData, InvitationData } from "../types";
import { useAuth } from "@/contexts/AuthContext";

interface ProfileInfoFormProps {
  invitation: InvitationData;
  company: CompanyData;
  isLoading: boolean;
  onSubmit: (profileData: {
    firstName: string;
    lastName: string;
    companyRole: string;
    personalPhoneNumber: string;
    profilePhotoUrl: string | null;
  }) => void;
}

export const ProfileInfoForm = ({ invitation, company, isLoading, onSubmit }: ProfileInfoFormProps) => {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyRole, setCompanyRole] = useState("");
  const [personalPhoneNumber, setPersonalPhoneNumber] = useState("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      firstName,
      lastName,
      companyRole,
      personalPhoneNumber,
      profilePhotoUrl
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete Your Profile</CardTitle>
        <CardDescription>
          Please provide your information to complete joining {company.name}.
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="flex justify-center mb-4">
            <ProfilePhotoUpload
              onImageUpload={setProfilePhotoUrl}
              initialImage={profilePhotoUrl}
              userId={user?.id}
            />
          </div>
          
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                placeholder="Your first name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                placeholder="Your last name"
              />
            </div>
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="personalPhoneNumber">Personal Phone Number *</Label>
            <Input
              id="personalPhoneNumber"
              value={personalPhoneNumber}
              onChange={(e) => setPersonalPhoneNumber(e.target.value)}
              required
              placeholder="Your personal phone number"
              type="tel"
            />
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="companyRole">Your Role in {company.name} *</Label>
            <Input
              id="companyRole"
              value={companyRole}
              onChange={(e) => setCompanyRole(e.target.value)}
              placeholder="e.g. Project Manager, Estimator, Admin"
              required
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Joining Company..." : "Join Company"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};
